import { ChangeDetectorRef, ComponentRef } from '@angular/core';
import {
  AbcCommentsListComponent,
  DocumentsEditorCardComponent,
  GalleryButtons,
  PropertiesEditorFormComponent,
} from '@barthauer/abc/common-editors';
import { CompareHelper, ObjectHelper } from '@barthauer/abc/core';
import {
  DataService,
  ICoreDataEntity,
  IPersistable,
  ObjectTypeInfoViewModel,
  ValidateModel,
} from '@barthauer/abc/core/data';
import {
  _AbcGridComponent,
  AbcAccordionComponent,
  AbcDateBoxComponent,
  AbcGalleryComponent,
  AbcTabPanelComponent,
  AbcWindowComponent,
  MessageBoxButtons,
  MessageBoxModel,
} from '@barthauer/abc/ui-components';
import { Item } from 'devextreme/ui/accordion';
import { Item as TabPanelItem } from 'devextreme/ui/tab_panel';
import { Subject } from 'rxjs';
import { IDictionary, ValueWrapper, ValueWrapperViewModel } from '../../../helpers/valueWrapper';
import { ColumnObject, LayoutColumn } from '../../models/layout.model';
import { LayoutColumnViewModel, LayoutViewModel } from '../../viewModels/layout.viewModel';
import { DataFormService } from '../data-form.service';

export class DataFormSettings {
  private _listOfValueWrappers: IDictionary<ValueWrapper> = {};
  private _selectedObject: SelectedObject = { Object: null };
  private _objectKey: ValueWrapperViewModel;
  private _mainObjectTypeVM: ObjectTypeInfoViewModel;
  private _subLayoutViewsObjectTypeVMMap: Map<string, ObjectTypeInfoViewModel> = new Map();
  private _validatedSubLayoutViewsOpathMap: Map<string, ValidateModel> = new Map();
  private _htmlGroups: Array<{ cbxGroupDataSource: { name: string; caption: string }[]; html: HTMLElement }> = [];
  private _isSpecialTypeComment: boolean;
  private _abcContextMenu: { domElement: HTMLElement; componentRef: ComponentRef<any> };
  private _dateBoxListMap: Map<string, AbcDateBoxComponent> = new Map();
  private _isPendingChangesReverted: boolean = false;
  private _cbxDataSources: Array<{ data: object; columns: Array<LayoutColumn> }> = [];
  private _abcComponentInstances: Array<ColumnObject> = [];
  private _linkLabelsMap: Map<string, HTMLElement> = new Map();
  private _uploadedImage: any; //TODO check the uploaded image type.
  private _selectedImageIndex: number = 0;
  private _abcGalleryCptRef: {
    componentRef: ComponentRef<AbcGalleryComponent>;
    domElement: HTMLElement;
  };
  private _abcAccordionCptRef: {
    componentRef: ComponentRef<AbcAccordionComponent>;
    domElement: HTMLElement;
  };
  private _abcTabsCptRef: {
    componentRef: ComponentRef<AbcTabPanelComponent>;
    domElement: HTMLElement;
  };

  private _isRefColumnsIds: Array<string> = [];
  private _windowInstance: AbcWindowComponent;
  private _accordionDataSource: Array<Item> = [];
  private _tabsDataSource: Array<TabPanelItem> = [];

  public takePictureSubject: Subject<{ imageUrl: string; isGalery: boolean }> = new Subject();
  public takePicture$ = this.takePictureSubject.asObservable();
  public validatedSubLayoutViewsOpath: Map<string, ValidateModel> = new Map();
  public gridOnFormInstances: _AbcGridComponent[] = [];
  public isPropertiesDisabled: boolean = false;
  public isCommentsDisabled: boolean = false;
  public isDocumentsDisabled: boolean = false;
  public isPropertiesAvailable: boolean = false;
  public isCommentsAvailable: boolean = false;
  public isDocumentsAvailable: boolean = false;
  public isPropertiesChecked: boolean = false;
  public isCommentsChecked: boolean = false;
  public isDocumentsChecked: boolean = false;
  public readonly customComboBoxColumns: LayoutColumnViewModel[] = [];
  public galleryMutationObserver: MutationObserver;

  public readonly DISPLAYTEXT_HIDDEN = 'DisplayText___HIDDEN';
  public readonly placeholderPath: string = 'assets/images/placeholderCamera.svg';
  public destroyObservables: Subject<void> = new Subject();
  private _validatorActions: ((isActive: boolean) => void)[] = [];

  public get listOfValueWrappers(): IDictionary<ValueWrapper> {
    return this._listOfValueWrappers;
  }
  public set listOfValueWrappers(value: IDictionary<ValueWrapper>) {
    if (CompareHelper.isEmptyObject(value)) {
      console.log(value);
    }
    this._listOfValueWrappers = value;
  }
  public get layoutViewModel(): LayoutViewModel {
    return this._layoutViewModel;
  }
  public set layoutViewModel(value: LayoutViewModel) {
    this._layoutViewModel = value;
  }
  public get selectedObject(): SelectedObject {
    return this._selectedObject;
  }
  public set selectedObject(value: SelectedObject) {
    this._selectedObject = value;
  }
  public get objectKey(): ValueWrapperViewModel {
    return this._objectKey;
  }
  public set objectKey(value: ValueWrapperViewModel) {
    this._objectKey = value;
  }
  public get mainObjectTypeViewModel(): ObjectTypeInfoViewModel {
    return this._mainObjectTypeVM;
  }
  public set mainObjectTypeViewModel(value: ObjectTypeInfoViewModel) {
    this._mainObjectTypeVM = value;
  }
  public get subLayoutViewsObjectTypeVMMap(): Map<string, ObjectTypeInfoViewModel> {
    return this._subLayoutViewsObjectTypeVMMap;
  }
  public set subLayoutViewsObjectTypeVMMap(value: Map<string, ObjectTypeInfoViewModel>) {
    this._subLayoutViewsObjectTypeVMMap = value;
  }
  public get validatedSubLayoutViewsOpathMap(): Map<string, ValidateModel> {
    return this._validatedSubLayoutViewsOpathMap;
  }
  public set validatedSubLayoutViewsOpathMap(value: Map<string, ValidateModel>) {
    this._validatedSubLayoutViewsOpathMap = value;
  }
  public get htmlGroups(): Array<{ cbxGroupDataSource: { name: string; caption: string }[]; html: HTMLElement }> {
    return this._htmlGroups;
  }
  public set htmlGroups(value: Array<{ cbxGroupDataSource: { name: string; caption: string }[]; html: HTMLElement }>) {
    this._htmlGroups = value;
  }

  public get accordionDataSource(): Array<Item> {
    return this._accordionDataSource;
  }
  public set accordionDataSource(value: Array<Item>) {
    this._accordionDataSource = value;
  }

  public get tabsDataSource(): Array<TabPanelItem> {
    return this._tabsDataSource;
  }
  public set tabsDataSource(value: Array<TabPanelItem>) {
    this._tabsDataSource = value;
  }

  public pushToHtmlGroups(object: { cbxGroupDataSource: { name: string; caption: string }[]; html: HTMLElement }) {
    this._htmlGroups.push(object);
  }

  public pushToAcordionDataSource(object: Item) {
    this._accordionDataSource.push(object);
  }

  public pushToTabsDataSource(object: TabPanelItem) {
    this._tabsDataSource.push(object);
  }

  public get isSpecialTypeComment(): boolean {
    return this._isSpecialTypeComment;
  }
  public set isSpecialTypeComment(value: boolean) {
    this._isSpecialTypeComment = value;
  }
  public get dateBoxListMap(): Map<string, AbcDateBoxComponent> {
    return this._dateBoxListMap;
  }
  public set dateBoxListMap(value: Map<string, AbcDateBoxComponent>) {
    this._dateBoxListMap = value;
  }
  public get isPendingChangesReverted(): boolean {
    return this._isPendingChangesReverted;
  }
  public set isPendingChangesReverted(value: boolean) {
    this._isPendingChangesReverted = value;
  }
  public get cbxDataSources(): Array<{ data: object; columns: Array<LayoutColumn> }> {
    return this._cbxDataSources;
  }
  public set cbxDataSources(value: Array<{ data: object; columns: Array<LayoutColumn> }>) {
    this._cbxDataSources = value;
  }
  public get abcComponentInstances(): Array<ColumnObject> {
    return this._abcComponentInstances;
  }
  public set abcComponentInstances(value: Array<ColumnObject>) {
    this._abcComponentInstances = value;
  }
  public get linkLabelsMap(): Map<string, HTMLElement> {
    return this._linkLabelsMap;
  }
  public set linkLabelsMap(value: Map<string, HTMLElement>) {
    this._linkLabelsMap = value;
  }
  public get uploadedImage(): any {
    return this._uploadedImage;
  }
  public set uploadedImage(value: any) {
    this._uploadedImage = value;
  }
  public get selectedImageIndex(): number {
    return this._selectedImageIndex;
  }
  public set selectedImageIndex(value: number) {
    this._selectedImageIndex = value;
  }
  public get abcGalleryCptRef(): {
    componentRef: ComponentRef<any>;
    domElement: HTMLElement;
  } {
    return this._abcGalleryCptRef;
  }
  public set abcGalleryCptRef(value: { componentRef: ComponentRef<any>; domElement: HTMLElement }) {
    this._abcGalleryCptRef = value;
  }

  public get abcAccordionCptRef(): {
    componentRef: ComponentRef<any>;
    domElement: HTMLElement;
  } {
    return this._abcAccordionCptRef;
  }
  public set abcAccordionCptRef(value: { componentRef: ComponentRef<any>; domElement: HTMLElement }) {
    this._abcAccordionCptRef = value;
  }

  public get abcTabsCptRef(): {
    componentRef: ComponentRef<any>;
    domElement: HTMLElement;
  } {
    return this._abcTabsCptRef;
  }
  public set abcTabsCptRef(value: { componentRef: ComponentRef<any>; domElement: HTMLElement }) {
    this._abcTabsCptRef = value;
  }

  public get isRefColumnsIds(): Array<string> {
    return this._isRefColumnsIds;
  }
  public set isRefColumnsIds(value: Array<string>) {
    this._isRefColumnsIds = value;
  }
  public get windowInstance(): AbcWindowComponent {
    return this._windowInstance;
  }
  public set windowInstance(value: AbcWindowComponent) {
    this._windowInstance = value;
  }

  public get isPDCChecked(): boolean {
    return this.isCommentsChecked || this.isDocumentsChecked || this.isPropertiesChecked;
  }
  //TODO change if possible
  public groupList: { name: string; caption: string }[] = [];

  public commonEditorsComponents = [
    {
      text: this.service.arena.res('DataForm-Documents'),
      component: DocumentsEditorCardComponent,
      name: 'Documents',
    },
    {
      text: this.service.arena.res('DataForm-Comments'),
      component: AbcCommentsListComponent,
      name: 'Comments',
    },
    {
      text: this.service.arena.res('DataForm-Properties'),
      component: PropertiesEditorFormComponent,
      name: 'Properties',
    },
  ];
  public messageButtons: MessageBoxButtons[] = [
    {
      text: GalleryButtons.Kamera,
      onClick: (e) => {
        return { buttonText: e.component.option('text') };
      },
    },
    {
      text: GalleryButtons.Galerie,
      onClick: (e) => {
        // TODO check if it's working
        document.getElementById('dataFormUploadImageInput')?.click();
        // this.el.nativeElement.click();
        return { buttonText: e.component.option('text') };
      },
    },
    {
      text: GalleryButtons.Abbrechen,
      onClick: (e) => {
        return { buttonText: e.component.option('text') };
      },
    },
  ];
  public messageBox: MessageBoxModel = {
    title: 'Bild hinzufügen',
    contentText: 'Bitte wählen Sie eine Bildquelle aus.',
    buttons: this.messageButtons,
  };

  public setReferencingColumn(objectKey: IPersistable, displayTextValues?: Map<string, string>): void {
    const target = this.service.abcArena.actions.getLayoutContext()?.target;
    const referencingColumnName = target?.column;
    if (referencingColumnName) {
      const refObjectTypeValue = target.sourceObjectType;
      const mainId = target.sourceId;
      let parentKey: string;
      for (const e of ObjectHelper.asArray(this.layoutViewModel.columnsObjectMap)) {
        const arr = e[1];
        for (const c of arr) {
          if (c.LayoutColumn?.OPath === referencingColumnName) {
            parentKey = c.Key;
            break;
          }
        }
      }
      if (referencingColumnName?.startsWith('Parent')) {
        const refObjectTypeColumnName = referencingColumnName.replace('Id', 'ObjectType');

        if (this.mainObjectTypeViewModel.columnsByName.has(refObjectTypeColumnName)) {
          const otValueWrapper = new ValueWrapper(
            refObjectTypeColumnName,
            refObjectTypeColumnName,
            refObjectTypeValue,
            objectKey,
            this.service.persistence
          );
          this.listOfValueWrappers[refObjectTypeColumnName] = otValueWrapper;
          this.listOfValueWrappers[refObjectTypeColumnName].value = refObjectTypeValue;
          this.service.persistence.addObjectValue(objectKey, refObjectTypeColumnName, refObjectTypeValue);
        }
      }
      if (parentKey) {
        const displayText = displayTextValues?.get(referencingColumnName);
        if (displayText) {
          const wrapper = this.listOfValueWrappers[this.service.DISPLAYTEXT_PREFIX + parentKey];
          if (wrapper) {
            wrapper.value = displayText;
          }
        }

        const valueWrapper = new ValueWrapper(
          parentKey,
          referencingColumnName,
          mainId,
          objectKey,
          this.service.persistence,
          displayText
        );
        this.listOfValueWrappers[parentKey] = valueWrapper;
        this.listOfValueWrappers[parentKey].value = mainId;
        this.objectKey.listOfValueWrappers = this.listOfValueWrappers;
      }
      if (referencingColumnName) {
        this.service.persistence.addObjectValue(objectKey, referencingColumnName, mainId);
      }
    }
  }

  public setParentReferencingColumn(objectKey: IPersistable, displayTextValues?: Map<string, string>) {
    if (displayTextValues?.size > 0) {
      let parentKey: string;
      let parentDisplayText: string;
      let parentColumnName: string;
      let columnObject: ColumnObject;
      for (const e of ObjectHelper.asArray(this.layoutViewModel.complexOpathColumnsObjectMap)) {
        const array = e[1];
        for (const column of array) {
          for (const dtv of ObjectHelper.asArray(displayTextValues)) {
            if (column.Key === dtv[0]) {
              parentKey = column.Key;
              parentDisplayText = dtv[1];
              parentColumnName = column.LayoutColumn?.OPath;
              columnObject = column;
              break;
            }
          }
        }
      }

      if (parentKey) {
        const displayText = parentDisplayText;
        if (displayText) {
          const wrapper = this.listOfValueWrappers[parentKey];
          if (wrapper) {
            wrapper.initialValue = displayText;
            if ('value' in columnObject.Control) {
              columnObject.Control['value'] = displayText;
            }
          }
        }
        this.objectKey.listOfValueWrappers = this.listOfValueWrappers;
      }
    }
  }

  public setCurrentObjectFunctions: Array<(object: ICoreDataEntity) => Promise<void>> = [];
  public isGridOnFormOnEditing: boolean = false;

  constructor(
    private service: DataFormService,
    private _layoutViewModel: LayoutViewModel,
    public filter: string,
    private changeDetectorRef: ChangeDetectorRef
  ) {}

  public get data(): DataService {
    return this.service.core.data;
  }

  public setCurrentColumnValue(column: LayoutColumnViewModel, value: any) {
    const wrapper = this.listOfValueWrappers[column.dataField];

    if (wrapper) {
      wrapper.value = value;
    } else {
      console.error('ValueWrapper not found: ' + column.dataField);
    }
  }

  public detectChanges() {
    this.changeDetectorRef.detectChanges();
  }

  public appendValidatorAction(action: (isActive: boolean) => void) {
    this._validatorActions.push(action);
  }

  public setValidationActive(isActive: boolean): void {
    for (const action of this._validatorActions) {
      action(isActive);
    }
  }
}

export interface SelectedObject {
  [x: string]: unknown;
  Object: ICoreDataEntity;
}

export interface UIComponent<T> {
  domElement: HTMLElement;
  componentInstance: T;
}
